/**
 * @fileoverview Arquivo responsável por definir as traduções em inglês da aplicação.
 */

const messages = {
	en: {
		translations: {
			selectLanguage: "Select Language",
			onboarding: {
				title: "Account setup",
				subtitle: "Finish the steps and start operating",
				minimize: "Minimize",
				requiredTag: "Required",
				of: "of",
				stepsDone: "steps completed",
				groups: {
					required: "Required",
					optional: "Optional (recommended)",
				},
				cta: {
					iniciar: "Start",
					continuar: "Continue",
					revisar: "Review",
					retomar: "Resume",
					pular: "Skip",
				},
				tour: {
					gotit: "Got it",
				},
				celebration: {
					taskDone: "Nice! One more step done 🎉",
					aha: "Congrats! Your AI was created successfully",
					allDone: "Awesome! You finished the main onboarding steps 🏆",
				},
				trial: {
					prefix: "Trial period:",
					daysLeft: "days left",
					dayLeft: "day left",
					lastDay: "last day",
				},
				blocked: {
					title: "Account paused",
					titleCancelled: "Account cancelled",
					desc: "Your trial has ended and we couldn't find a confirmed payment. Settle it to keep using Sttelar Chat.",
					descCancelled: "This account has been cancelled. Contact us to reactivate it.",
					payCta: "Settle payment",
					updateCard: "Update payment method",
					contact: "Contact support to settle your account.",
					pausedAdminContact: "Talk to your account administrator to settle the payment and restore access.",
				},
				checkout: {
					title: "Choose your plan",
					desc: "Your free trial has ended. Pick a plan to keep using Sttelar Chat.",
					basic: "Basic",
					pro: "Pro",
					basicDesc: "Organized human support",
					proDesc: "AI + human support",
					aiSeatNote: "AI counts as 1 user",
					aiSeatTooltip: "On the Pro plan, the AI is billed as 1 plan user. E.g.: 2 human agents + AI = 3 billable users.",
					features: {
						basic: [
							"Chatbot flows",
							"Detailed reports",
							"Unlimited quick replies",
							"WhatsApp-integrated CRM",
							"Centralized service"
						],
						pro: [
							"Everything in Basic",
							"24/7 service with AI",
							"Custom AI for your business"
						]
					},
					monthly: "Monthly",
					annual: "Annual",
					annualSave: "−16%",
					users: "Users",
					minNote: "Minimum billing of 3 users",
					addon: "Internal Chat",
					addonHint: "Internal team communication (per user)",
					billable: "billable users",
					perMonth: "/mo",
					cta: "Continue to payment",
					cardTitle: "Payment",
					cardDesc: "Recurring charge on your credit card.",
					cardNumber: "Card number",
					cardHolder: "Name on card",
					expMonth: "Month",
					expYear: "Year",
					expiry: "Expiry",
					acceptedBrands: "Accepted cards",
					cvv: "CVV",
					pay: "Pay",
					processing: "Processing...",
					fillCard: "Fill in all card details.",
					payError: "Could not complete the payment",
					successTitle: "Payment received!",
					confirmingTitle: "Confirming payment...",
					successDesc: "Your subscription was created. Your account will be unlocked shortly.",
					releasingDesc: "We're confirming your payment. Your account will be unlocked shortly.",
					stillReleasingDesc: "We received your payment. Unlocking may take a few more moments — you can already access it.",
					releasedTitle: "Account unlocked!",
					releasedDesc: "All set! You now have full access to Sttelar Chat.",
					access: "Access now",
					closeNow: "Close",
					paymentMethod: "Payment method",
					payCard: "Credit card",
					payPix: "PIX",
					pixOnlyAnnualShort: "Annual plan only",
					pixTitle: "Pay with PIX",
					pixDesc: "Scan the QR code or copy the code. Your account is released automatically after payment.",
					pixCopy: "Copy code",
					pixCopied: "Copied!",
					pixWaiting: "Waiting for payment...",
					back: "Back",
					annualCharge: "Billed per year",
					secureNote: "Securely processed by Pagar.me. We don't store your card data.",
					stepPlan: "Plan",
					stepBilling: "Billing",
					stepPayment: "Payment",
					next: "Continue",
					toBilling: "Continue to billing",
					toPayment: "Continue to payment",
					billingTitle: "Billing details",
					billingDesc: "Used for invoicing and payment security.",
					document: "Tax ID (CPF/CNPJ)",
					phone: "Phone",
					cep: "ZIP code",
					street: "Address",
					number: "Number",
					complement: "Complement",
					neighborhood: "District",
					city: "City",
					state: "State",
					optional: "Optional",
					fillBilling: "Fill in the required billing details.",
					supportWhatsappMessage: "I'm on the Sttelar Chat plan selection step and I have a question",
					supportTooltip: "Ask Sttelar support on WhatsApp",
					logoutTooltip: "Log out of your account",
					logout: "Log out",
					documentPlaceholder: "Tax ID (CPF/CNPJ)",
					numberPlaceholder: "No.",
					streetPlaceholder: "Street",
					statePlaceholder: "State",
					cardHolderPreview: "NAME ON CARD",
					cardHolderPlaceholder: "NAME AS ON CARD",
					pixQrAlt: "PIX QR code",
					aiSeatSuffix: " (+AI)",
				},
				upgrade: {
					loadError: "Could not load the plan details.",
					confirmError: "Could not complete the upgrade",
					titleInternal: "Add Internal Chat",
					titleAttendance: "Add users",
					descInternal: "Increase the number of Internal Chat users in your plan.",
					descAttendance: "Increase the number of users in your plan.",
					descNextCharge: " The new amount will apply to your next charge.",
					close: "Close",
					successTitle: "Seats updated!",
					successInternal: "You now have {{count}} Internal Chat seats.",
					successAttendance: "You now have {{count}} users in your plan.",
					planUnavailable: "Plan unavailable.",
					planRowInternal: "{{tier}} plan · {{ciclo}} · today {{count}} Internal Chat",
					planRowAttendance: "{{tier}} plan · {{ciclo}} · today {{count}} {{usersWord}}",
					cycleAnnual: "Annual",
					cycleMonthly: "Monthly",
					userSingular: "user",
					userPlural: "users",
					newTotalInternal: "New Internal Chat total",
					newTotalAttendance: "New user total",
					minHint: "Minimum {{min}} (1 more than current)",
					perMonth: "per month",
					billedAnnually: " · billed annually",
					breakInternal: "{{addon}} Internal Chat · {{seats}} billable{{ai}}",
					breakAttendance: "{{seats}} billable seats{{ai}}",
					aiSuffix: " (+AI)",
					processing: "Processing...",
					confirm: "Confirm upgrade",
					footnote: "The extra amount is charged starting next cycle. You can reduce it later by contacting support.",
				},
				changeCard: {
					notConfigured: "Payment not configured (public key missing).",
					fillAllFields: "Fill in all the card details.",
					saveError: "Could not change the card",
					title: "Change card",
					desc: "The new card will be used for your next subscription charges.",
					close: "Close",
					successTitle: "Card updated!",
					numberLabel: "Card number",
					holderLabel: "Cardholder name",
					holderPlaceholder: "As printed on the card",
					expiryLabel: "Expiry",
					expiryPlaceholder: "MM/YY",
					cvvLabel: "CVV",
					processing: "Processing...",
					save: "Save card",
					secureNote: "Your data goes directly and encrypted to Pagar.me — it never passes through our servers.",
				},
				tasks: {
					createAi: { label: "Create your company AI", desc: "Build your smart agent and test it instantly" },
					connectWhatsapp: { label: "Connect WhatsApp", desc: "Connect a number to start serving" },
					transferRule: { label: "Handoff-to-human rule", desc: "Define when the AI hands off to an agent" },
					registerUsers: { label: "Add users", desc: "Add other users who will serve through Sttelar Chat" },
					businessHours: { label: "Business hours", desc: "Set your operation's schedule" },
					quickMessage: { label: "Quick reply", desc: "Create ready-made replies to speed things up" },
					sectorsQueues: { label: "Create sectors", desc: "Organize service into queues/sectors" },
					tags: { label: "Set up tags", desc: "Classify your conversations with tags" },
					crmBoards: { label: "Set up boards (CRM)", desc: "Manage leads and tasks in the Kanban" },
					academy: { label: "Finish training", desc: "Learn the platform in the Academy" },
				},
				aha: {
					title: "Create your company AI",
					subtitle: "Fill in the fields and build your smart agent",
					create: "Create my AI",
					cancel: "Cancel",
					done: "Done",
					requiredWarning: "Please provide at least the AI name and the segment.",
					successTitle: "Your AI is ready!",
					successSubtitle: "Try a question right now",
					successMessage: "Done! Your AI has been created and can already answer your first customers based on the information you provided.",
					fallbackNote: "To test the AI here, set up an OpenAI integration under Integrations. The AI is already created and will be ready once the key is configured.",
					fields: {
						nomeIA: { label: "AI name", placeholder: "e.g., Sttela" },
						segmento: { label: "Company segment", placeholder: "e.g., Dental clinic" },
						tom: { label: "Service tone", placeholder: "Choose or describe the tone" },
						produtosServicos: { label: "Main products or services", placeholder: "What do you offer?" },
						duvidasFrequentes: { label: "Frequently asked questions", placeholder: "What do customers ask the most?" },
						regrasAtendimento: { label: "Basic service rules", placeholder: "e.g., always confirm name and phone" },
						infosImportantes: { label: "Info the AI should know", placeholder: "Hours, address, policies..." },
						situacoesTransferencia: { label: "When to hand off to a human", placeholder: "e.g., complaints or complex requests" },
					},
				},
			},
			signup: {
				title: "Sign Up",
				toasts: {
					success: "User created successfully! Please login!!!",
					fail: "Error creating user. Please check the provided information.",
				},
				form: {
					name: "Company Name",
					email: "Email",
					phone: "Phone Number (with Area Code)",
					plan: "Plan",
					password: "Password",
				},
				formErrors: {
					name: {
						required: "Company name is required",
						short: "Name is too short",
						long: "Name is too long",
					},
					password: {
						short: "Password is too short",
						long: "Password is too long",
					},
					email: {
						required: "Email is required",
						invalid: "Invalid email",
					},
				},
				buttons: {
					submit: "Register",
					login: "Already have an account? Login!",
				},
				plan: {
					attendant: "Attendant",
					whatsapp: "WhatsApp",
					queues: "Queues",
				},
			},
			login: {
				title: "Login",
				form: {
					email: "Email",
					password: "Password",
				},
				buttons: {
					submit: "Sign in",
					submitting: "Signing in...",
					register: "Sign up now!",
				},
				errors: {
					emailRequired: "Email is required",
					emailInvalid: "Invalid email",
					passwordRequired: "Password is required",
				},
				capsLockOn: "Caps Lock is on",
			},
			mfa: {
				setup: {
					title: "Set up two-factor authentication",
					scanQr: "Scan the QR code with Google Authenticator, Duo Mobile or a compatible app.",
					manualSecret: "Can't scan? Enter this code manually:",
					next: "Next",
					back: "Back",
					confirmOtp: "Enter the 6-digit code from the app:",
					confirm: "Confirm",
					backupTitle: "Backup codes",
					backupHint: "Store them safely. Each code is single-use and replaces the app if you lose your phone.",
					copyAll: "Copy all",
					download: "Download .txt",
					savedAck: "I have saved my codes securely",
					oneTimeWarning: "These codes will be shown only once.",
					finish: "Done",
					cancel: "Cancel",
					copied: "Codes copied",
				},
				login: {
					title: "Two-factor verification",
					otpLabel: "Authenticator app code",
					otpHint: "Enter the 6-digit code from your app.",
					backupLabel: "Backup code",
					backupHint: "Use one of the recovery codes you saved.",
					verify: "Verify",
					useBackup: "Use backup code",
					useOtp: "Use authenticator app",
					backToPassword: "Back",
				},
				policy: {
					title: "Company MFA policy",
					modeOff: "Disabled",
					modeOptional: "Optional",
					modeRequired: "Required",
					gracePeriod: "Setup deadline (days)",
					saved: "Policy updated",
					description: "Controls whether users in this company must enable two-factor authentication.",
				},
				profile: {
					menuItem: "Two-factor authentication",
					statusEnabled: "Active since {{date}}",
					statusDisabled: "Not configured",
					enable: "Enable",
					disable: "Disable",
					disableConfirm: "Confirm with your current code to disable MFA",
					adminReset: "Reset user MFA",
					adminResetConfirm: "Are you sure? The user will need to re-enroll on next login.",
					adminResetDone: "MFA has been reset",
					enabledDesc: "Your account is protected: every login requires a code from your authenticator app.",
					disabledDesc: "Enable two-factor authentication to strengthen your account security. You'll need an app like Google Authenticator or Duo Mobile.",
				},
				banner: {
					title: "Your company requires two-factor authentication.",
					subtitle: "Set it up within {{days}} day(s) to keep accessing the platform.",
					cta: "Set up now",
				},
				errors: {
					invalidOtp: "Invalid or expired code",
					ticketExpired: "Verification session expired. Please log in again.",
					backupUsed: "Backup code already used",
				},
			},
			resetPassword: {
				title: "Reset Password",
				toasts: {
					emailSent: "Email sent successfully!",
					emailNotFound: "Email not found!",
					passwordUpdated: "Password updated successfully!",
				},
				formErrors: {
					email: {
						required: "Email is required",
						invalid: "Invalid email",
					},
					newPassword: {
						required: "New password is required",
						matches: "Your password must have at least 8 characters, including one uppercase letter, one lowercase letter, and one number.",
					},
					confirmPassword: {
						required: "Password confirmation is required",
						matches: "Passwords do not match",
					},
				},
				form: {
					email: "Email",
					verificationCode: "Verification Code",
					newPassword: "New Password",
					confirmPassword: "Confirm New Password",
				},
				buttons: {
					submitEmail: "Send Email",
					submitPassword: "Reset Password",
					back: "Don't have an account? Sign up!",
				},
			},
			dashboard: {
				toasts: {
					selectFilterError: "Set filter parameters",
					userChartError: "Error getting conversation information",
					dateChartError: "Error getting conversation information",
				},
				filters: {
					initialDate: "Start Date",
					finalDate: "End Date",
					filterType: {
						title: "Filter Type",
						options: {
							perDate: "Filter by Date",
							perPeriod: "Filter by Period",
						},
						helper: "Select desired filter type",
					},
				},
				periodSelect: {
					title: "Period",
					options: {
						none: "None selected",
						last3: "Last 3 days",
						last7: "Last 7 days",
						last15: "Last 15 days",
						last30: "Last 30 days",
						last60: "Last 60 days",
						last90: "Last 90 days",
					},
					helper: "Select desired period",
				},
				counters: {
					inTalk: "In conversation",
					waiting: "Waiting",
					finished: "Finished",
					newContacts: "New contacts",
					averageTalkTime: "Avg. Talk Time",
					averageWaitTime: "Avg. Wait Time",
				},
				buttons: {
					filter: "Filter",
				},
				onlineTable: {
					ratingLabel: "1 - Unsatisfied, 2 - Satisfied, 3 - Very Satisfied",
					name: "Name",
					ratings: "Ratings",
					avgSupportTime: "Avg. Support Time",
					status: "Status (Current)",
				},
				charts: {
					user: {
						label: "Conversations Chart",
						title: "Total Conversations by Users",
						start: "Start",
						end: "End",
						filter: "Filter",
					},
					date: {
						label: "Conversations Chart",
						title: "Total",
						start: "Start",
						end: "End",
						filter: "Filter",
					},
				},
			},
			dashboardGeral: {
				title: "Group Dashboard",
				subtitle: "Consolidated view of group {{groupName}}",
				accountsCount: "{{count}} accounts",
				cards: {
					active: "Active Tickets",
					pending: "Pending",
					noResponse: "No Response +10min",
					noResponseRate: "No response rate",
					avgPerAttendant: "Average/Attendant",
					art: "Group ART",
				},
				table: {
					title: "Performance by Account",
					headers: {
						account: "Account",
						active: "Active",
						pending: "Pending",
						noResponse: "No Resp +10min",
						art: "ART",
						attendants: "Attendants",
					},
				},
				states: {
					loading: "Loading group dashboard...",
					empty: "No accounts found in this group",
					error: "Error loading dashboard. Try again?",
				},
			},
			plans: {
				toasts: {
					errorList: "Could not load records list",
					errorOperation: "Could not complete operation",
					error: "Could not complete operation. Check if a plan with the same name already exists or if fields were filled correctly",
					success: "Operation completed successfully!",
				},
				confirm: {
					title: "Delete Record",
					message: "Do you really want to delete this record?",
				},
				buttons: {
					newCategory: "New Category",
					newPlan: "New Plan",
				},
				modal: {
					editPlan: "Edit Plan",
					newPlan: "New Plan",
					newCategory: "New Category",
					categoryName: "Category Name",
					categoryPlaceholder: "E.g.: Premium, Enterprise, Starter",
					basicInfo: "Basic Information",
					emptyCategory: "This category is empty. Click \"New Plan\" to add.",
				},
				labels: {
					plans: "Plans",
					plan: "plan",
					plansPlural: "plans",
				},
				form: {
					name: "Name",
					users: "Users",
					connections: "Connections",
					queues: "Queues",
					value: "Value",
					internalChat: "Internal Chat",
					externalApi: "External API",
					kanban: "CRM",
					integrations: "Integrations",
					campaigns: "Campaigns",
					schedules: "Schedules",
					leads: "CRM",
					enabled: "Enabled",
					disabled: "Disabled",
					clear: "Cancel",
					delete: "Delete",
					save: "Save",
					yes: "Yes",
					no: "No",
					money: "$",
				},
			},
			externalLeads: {
				title: "CRM",
				refresh: "Refresh",
				stats: {
					total: "Total Leads",
					unviewed: "New",
					pending: "Pending",
					completed: "Completed",
				},
				filters: {
					searchPlaceholder: "Search by name, phone, ID or service order...",
					status: "Status",
					all: "All",
					sortBy: "Sort by",
					newestFirst: "Newest first",
					oldestFirst: "Oldest first",
				},
				card: {
					new: "New",
					notInformed: "Not informed",
					attachments: "attachment(s)",
					serviceOrderPlaceholder: "Service Order",
					saveServiceOrder: "Save S.O.",
					noChanges: "No changes",
					viewDetails: "View details",
					delete: "Delete",
					startConversation: "Start WhatsApp conversation",
				},
				modal: {
					clientInfo: "Client Information",
					name: "Name",
					phone: "Phone",
					email: "E-mail",
					cpf: "ID Number",
					address: "Address",
					serviceOrder: "Service Order",
					attachments: "Attachments",
					customFields: "Custom Fields",
					changeStatus: "Change Status",
					receivedAt: "Received at:",
					viewedAt: "Viewed at:",
					close: "Close",
				},
				confirmDelete: {
					title: "Delete Lead",
					message: "Are you sure you want to delete this lead? This action cannot be undone.",
				},
				empty: {
					title: "No leads found",
					description: "Leads received via webhook will appear here",
				},
				loading: "Loading...",
				toasts: {
					newLead: "New lead received:",
					deleted: "Lead deleted successfully",
					statusUpdated: "Status updated!",
					serviceOrderSaved: "Service Order saved!",
				},
				defaultStatus: "Pending",
			},
			kanban: {
				title: "CRM Kanban",
				toasts: {
					removed: "Tag removed from ticket!",
					added: "Tag added successfully!",
					refreshed: "Data updated!",
					errorLoadingTags: "Error loading tags",
					errorLoadingTickets: "Error loading tickets",
					errorMoving: "Error moving card",
				},
				open: "Open",
				seeTicket: "View Ticket",
				tickets: "tickets",
				columns: "columns",
				refresh: "Refresh",
				searchPlaceholder: "Search by name or number...",
				allQueues: "All queues",
				loading: "Loading tickets...",
				noMessage: "No message",
				unknownContact: "Unknown contact",
			},
			companies: {
				title: "Register Company",
				buttons: {
					newGroup: "New Group",
					newCompany: "New Company",
				},
				modal: {
					editCompany: "Edit Company",
					newCompany: "New Company",
					newGroup: "New Group",
					groupName: "Group Name",
					groupPlaceholder: "E.g.: Client XYZ",
				},
				labels: {
					companies: "Companies",
					company: "company",
					companiesPlural: "companies",
				},
				tooltips: {
					addUser: "Add new user",
					editUser: "Edit existing user",
					editCompany: "Edit company",
					deleteCompany: "Delete company",
				},
				form: {
					name: "Company Name",
					plan: "Plan",
					token: "Token",
					submit: "Register",
					success: "Company created successfully!",
				},
			},
			auth: {
				toasts: {
					success: "Login successful!",
				},
				token: "Token",
			},
			connections: {
				title: "Connections",
				toasts: {
					deleted: "WhatsApp connection deleted successfully!",
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "Are you sure? This action cannot be undone.",
					disconnectTitle: "Disconnect",
					disconnectMessage: "Are you sure? You'll need to scan the QR Code again.",
				},
				buttons: {
					add: "Add Connection",
					disconnect: "disconnect",
					tryAgain: "Try again",
					qrcode: "QR CODE",
					newQr: "New QR CODE",
					connecting: "Connecting",
				},
				toolTips: {
					disconnected: {
						title: "Failed to start WhatsApp session",
						content: "Make sure your phone is connected to the internet and try again, or request a new QR Code",
					},
					qrcode: {
						title: "Waiting for QR Code scan",
						content: "Click the 'QR CODE' button and scan the QR Code with your phone to start the session",
					},
					connected: {
						title: "Connection established!",
					},
					timeout: {
						title: "Connection to phone was lost",
						content: "Make sure your phone is connected to the internet and WhatsApp is open, or click the 'Disconnect' button to get a new QR Code",
					},
				},
				table: {
					name: "Name",
					status: "Status",
					lastUpdate: "Last update",
					default: "Default",
					actions: "Actions",
					session: "Session",
				},
			},
			whatsappModal: {
				title: {
					add: "Add WhatsApp",
					edit: "Edit WhatsApp",
				},
				formErrors: {
					name: {
						required: "Name is required",
						short: "Name is too short",
						long: "Name is too long",
					},
					expiresTicket: {
						number: "Enter a valid number",
						integer: "Use integer values only",
						min: "Minimum is 0 (disabled)",
						max: "Maximum is 43200 minutes (30 days)",
					},
					expiresInactiveMessage: {
						max: "Message too long (max 1000 characters)",
					},
					greetingAcceptedMessage: {
						max: "Message too long (max 1000 characters)",
					},
				},
				tabs: {
					general: "General",
					messages: "Messages",
					assessments: "Assessments",
					integrations: "Integrations",
					schedules: "Business Hours",
				},
				form: {
					name: "Name",
					default: "Default",
					sendIdQueue: "Queue",
					timeSendQueue: "Redirect to queue in X minutes",
					queueRedirection: "Queue Redirection",
					outOfHoursMessage: "Out of office message",
					queueRedirectionDesc: "Select a queue for contacts without a queue to be redirected to",
					prompt: "Prompt",
					queue: "Transfer Queue",
					timeToTransfer: "Transfer after x (minutes)",
					metaInfo: "Provide the credentials from your Meta app to use the official WhatsApp Cloud API.",
					metaAccessToken: "Access Token",
					metaPhoneNumberId: "Phone Number ID",
					metaBusinessAccountId: "Business Account ID (optional)",
					metaAppId: "App ID (optional)",
					metaAppSecret: "App Secret",
					metaWebhookToken: "Webhook Verify Token",
					metaApiVersion: "Graph API Version",
					autoCloseTitle: "Auto close",
					autoCloseHelp: "Closes the chat when the client does not reply to the agent after the configured time. The message below is sent before closing.",
					expiresTicket: "Inactivity time",
					expiresTicketUnit: "minutes",
					expiresInactiveMessage: "Closure message",
					greetingMessage: "Greeting message",
					complationMessage: "Completion message",
					greetingAcceptedTitle: "Greeting on ticket accept",
					greetingAcceptedMessage: "Message on ticket accept",
					greetingAcceptedMessagePlaceholder: "e.g. {{ms}} *{{name}}*, my name is *{{userName}}* and I'll take it from here!",
					greetingAcceptedMessageHelp: "Sent when the agent accepts a pending ticket. Leave empty to use the default.",
					sendTransferQueueMessageTitle: "Message on queue transfer",
					sendTransferUserMessageTitle: "Message on agent transfer",
					sendTransferQueueAndUserMessageTitle: "Message on queue + agent transfer",
					integration: "Integration"
				},
				buttons: {
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
				},
				success: "WhatsApp saved successfully.",
			},
			qrCodeModal: {
				title: "Use your WhatsApp:",
				steps: {
					one: "1 - Open WhatsApp on your phone",
					two: {
						partOne: "2 - Tap More options on Android",
						partTwo: "or Settings",
						partThree: "on iPhone",
					},
					three: "3 - Tap Linked Devices and then Link a Device",
					four: "4 - Point your phone at this screen to capture the QR Code",
				},
				waiting: "Waiting for QR Code scan",
				tabs: {
					qr: "QR Code",
					pairing: "Pairing code",
				},
				pairing: {
					title: "Link with phone number",
					phoneLabel: "Phone number with country and area code",
					phonePlaceholder: "e.g. 15551234567",
					generate: "Generate code",
					generating: "Generating...",
					instructions: "On your phone's WhatsApp: Linked Devices → Link with phone number → enter the code below.",
					copy: "Copy",
					copied: "Copied!",
					invalidPhone: "Enter a valid phone number with country code.",
				},
			},
			qrCode: {
				message: "Scan the QR Code to start the session",
			},
			contacts: {
				title: "Contacts",
				toasts: {
					deleted: "Contact deleted successfully!",
					deletedAll: "All contacts deleted successfully!",
				},
				searchPlaceholder: "Search...",
				confirmationModal: {
					deleteTitle: "Delete ",
					deleteAllTitle: "Delete All",
					importTitle: "Import contacts",
					deleteMessage: "Are you sure you want to delete this contact? All related tickets will be lost.",
					deleteAllMessage: "Are you sure you want to delete all contacts? All related tickets will be lost.",
					importMessage: "Do you want to import all phone contacts?",
				},
					buttons: {
						import: "Import Contacts",
						add: "Add Contact",
						export: "Export Contacts",
						delete: "Delete All Contacts",
						startConversation: "Start WhatsApp conversation",
						edit: "Edit contact",
						deleteSingle: "Delete contact",
					},
				table: {
					name: "Name",
					whatsapp: "WhatsApp",
					email: "Email",
					actions: "Actions",
				},
			},
			contactImportModal: {
				title: "Contact Spreadsheet",
				labels: {
					import: "Import contacts",
					result: "results",
					added: "Added",
					savedContact: "Contact saved",
					errors: "Errors",
				},
				buttons: {
					download: "Download template spreadsheet",
					import: "Import contacts",
					background: "Continue in background",
					close: "Close",
				},
				progress: {
					importing: "Importing {{processed}} of {{total}} contacts...",
					success: "{{count}} imported successfully",
					errors: "{{count}} with errors",
					complete: "Import completed",
				},
			},
			queueIntegrationModal: {
				title: {
					add: "Add project",
					edit: "Edit project",
				},
				form: {
					id: "ID",
					type: "Type",
					name: "Name",
					projectName: "Project Name",
					language: "Language",
					jsonContent: "JsonContent",
					urlN8N: "URL",
					typebotSlug: "Typebot - Slug",
					typebotExpires: "Time in minutes to expire a conversation",
					typebotKeywordFinish: "Keyword to finish ticket",
					typebotKeywordRestart: "Keyword to restart flow",
					typebotRestartMessage: "Message when restarting conversation",
					typebotUnknownMessage: "Invalid option message",
					typebotDelayMessage: "Interval (ms) between messages",
				},
				buttons: {
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
					test: "Test Bot",
				},
				messages: {
					testSuccess: "Integration tested successfully!",
					addSuccess: "Integration added successfully.",
					editSuccess: "Integration edited successfully.",
				},
			},
			sideMenu: {
				name: "Initial Side Menu",
				note: "If enabled, the side menu will start closed",
				options: {
					enabled: "Open",
					disabled: "Closed",
				},
			},
			promptModal: {
				form: {
					name: "Name",
					prompt: "Prompt",
					model: "Model",
					max_tokens: "Maximum Tokens in response",
					temperature: "Temperature",
					apikey: "API Key",
					max_messages: "Maximum messages in History",
				},
				formErrors: {
					name: {
						short: "Name is too short",
						long: "Name is too long",
						required: "Name is required",
					},
					prompt: {
						short: "Prompt is too short",
						required: "Describe the training for Artificial Intelligence",
					},
					modal: {
						required: "Enter the desired model for the Prompt",
					},
					maxTokens: {
						required: "Enter the maximum number of tokens in the response",
					},
					temperature: {
						required: "Enter the temperature",
					},
					apikey: {
						required: "Enter the API Key",
					},
					queueId: {
						required: "Enter the queue",
					},
					maxMessages: {
						required: "Enter the maximum number of messages in history",
					},
				},
				success: "Prompt saved successfully!",
				setor: "Enter the sector",
				title: {
					add: "Add Prompt",
					edit: "Edit Prompt",
				},
				buttons: {
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
				},
			},
			prompts: {
				title: "Prompts",
				table: {
					name: "Name",
					queue: "Sector/Queue",
					max_tokens: "Maximum Response Tokens",
					actions: "Actions",
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "Are you sure? This action cannot be undone!",
				},
				buttons: {
					add: "Add Prompt",
				},
			},
			contactModal: {
				title: {
					add: "Add Contact",
					edit: "Edit Contact",
				},
				form: {
					mainInfo: "Contact Information",
					basicInfo: "Basic Information",
					complementaryInfo: "Additional Details",
					extraInfo: "Additional Information",
					name: "Name",
					number: "WhatsApp Number",
					email: "Email",
					extraName: "Field Name",
					extraValue: "Value",
					whatsapp: "Source Connection: ",
				},
				formErrors: {
					name: {
						required: "Name is required",
						short: "Name is too short",
						long: "Name is too long",
					},
					phone: {
						short: "Number is too short",
						long: "Number is too long",
					},
					email: {
						invalid: "Invalid email",
					},
				},
				buttons: {
					addExtraInfo: "Add Information",
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
				},
				success: "Contact saved successfully.",
			},
			queueModal: {
				title: {
					add: "Add Queue",
					edit: "Edit Queue",
				},
				form: {
					name: "Name",
					nameShort: "Short name",
					nameLong: "Long name",
					nameRequired: "Name is required",
					color: "Color",
					colorShort: "Short color",
					colorLong: "Long color",
					greetingMessage: "Greeting message",
					complationMessage: "Completion message",
					outOfHoursMessage: "Out of office message",
					ratingMessage: "Rating message",
					token: "Token",
					orderQueue: "Queue order (Bot)",
					integrationId: "Integration",
				},
				buttons: {
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
				},
				toasts: {
					success: "Queue saved successfully.",
					info: "Click save to register changes",
				},
				tabs: {
					queueData: "Queue Data",
					attendanceTime: "Service Hours",
				},
			},
			billing: {
				status: {
					teste_gratis: "Trial period",
					pago_ativo: "Active",
					aguarda_pagamento: "Awaiting payment",
					aguarda_assinatura: "Awaiting subscription",
					pagamento_vencido: "Payment overdue",
					bloqueado: "Blocked",
					cancelado: "Canceled",
					onboarding: "Setting up",
				},
				invoiceStatus: {
					paga: "Paid",
					pendente: "Pending",
					falhou: "Failed",
					cancelada: "Canceled",
				},
				exempt: {
					title: "Complimentary account",
					desc: "Free use — this account is exempt from billing.",
					badge: "Exempt",
				},
				cancelError: "We couldn't cancel right now. Please try again.",
				loadError: "We couldn't load your billing information right now.",
				retry: "Try again",
				noOrder: "No billing order found for this account.",
				productFallback: "Sttelar Chat",
				cycleAnnual: "Annual",
				cycleMonthly: "Monthly",
				perCycleAnnual: "/mo · billed annually",
				perCycleMonthly: "/mo",
				userSingular: "user",
				userPlural: "users",
				withInternalChat: " · + Internal Chat",
				amount: "Amount",
				trialEndsOn: "Trial ends on",
				daySingular: "day",
				dayPlural: "days",
				nextCharge: "Next charge",
				manageUsers: "Manage users",
				manageInternalChat: "Manage Internal Chat",
				cancelNotice: "Subscription canceled — you keep access until <strong>{{date}}</strong>. There will be no further charges.",
				pixNotice: "Annual plan via <strong>PIX</strong> · access until <strong>{{date}}</strong>. Renewal with a new PIX at the end of the period.",
				cancelSubscription: "Cancel subscription",
				paymentMethodTitle: "Payment method",
				noCard: "No card on file",
				changeCard: "Change card",
				invoicesTitle: "Invoice history",
				noInvoices: "No invoices issued yet.",
				billingDataTitle: "Billing details",
				name: "Name",
				email: "Email",
				document: "Tax ID (CPF/CNPJ)",
				phone: "Phone",
				address: "Address",
				zipPrefix: "ZIP",
				cancelDialogText: "Your subscription will stop renewing, but you <strong>keep access until {{date}}</strong> (end of the period already paid). There will be no further charges.",
				cancelDialogProAi: " The Pro plan AI stays active until then.",
				keepSubscription: "Keep subscription",
				cancelling: "Canceling...",
			},
			userModal: {
				loading: "Loading user data...",
				title: {
					add: "Add User",
					edit: "Edit User",
				},
				form: {
					name: "Name",
					email: "Email",
					cpf: "CPF",
					password: "Password",
					profile: "Profile",
					jobRole: "Job role",
					noJobRole: "No role",
					operationalType: "Operational type",
					attendant: "Attendant",
					supervisor: "Supervisor",
					whatsapp: "Default Connection",
					noWhatsapp: "No connection",
					queues: "User Queues",
					profileImage: "Change Photo",
					uploadPhoto: "Upload Photo",
					allTicket: "Queueless Ticket [Invisible]",
					allTicketEnabled: "Enabled",
					allTicketDesabled: "Disabled",
					accessScope: "Access Scope *",
					accessScopeFull: "Full (Support + Internal)",
					accessScopeAttendance: "Support Only (Tickets, WhatsApp)",
					accessScopeInternal: "Internal Only (Chat, CRM)",
					internalLockedNotice: "Internal Chat is not included in your plan. Upgrade in Settings → Billing to unlock it.",
				},
				formErrors: {
					name: {
						required: "Name is required",
						short: "Name is too short",
						long: "Name is too long",
					},
					password: {
						short: "Password is too short",
						long: "Password is too long",
					},
					email: {
						required: "Email is required",
						invalid: "Invalid email",
					},
				},
				labels: {
					liberations: "Releases",
				},
				buttons: {
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
				},
				success: "User saved successfully.",
			},
			scheduleModal: {
				title: {
					add: "New Schedule",
					edit: "Edit Schedule",
				},
				form: {
					body: "Message",
					contact: "Contact",
					sendAt: "Schedule Date",
					sentAt: "Send Date",
				},
				buttons: {
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
				},
				success: "Schedule saved successfully.",
			},
			tagModal: {
				title: {
					add: "New Tag",
					edit: "Edit Tag",
				},
				form: {
					name: "Name",
					color: "Color",
				},
				buttons: {
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
				},
				success: "Tag saved successfully.",
			},
			chat: {
				toasts: {
					fillTitle: "Please fill in the conversation title.",
					fillUser: "Please select at least one user.",
				},
				modal: {
					title: "Conversation",
					titleField: "Title",
				},
				confirm: {
					title: "Delete Conversation",
					message: "This action cannot be undone, confirm?",
				},
				chats: "Chats",
				messages: "Messages",
				noTicketMessage: "Select a ticket to start chatting.",
				buttons: {
					close: "Close",
					save: "Save",
					new: "New",
					newChat: "New",
				},
			},
			uploads: {
				titles: {
					titleUploadMsgDragDrop: "DRAG AND DROP FILES IN THE FIELD BELOW",
					titleFileList: "File List",
				},
			},
			ticketsManager: {
				buttons: {
					newTicket: "New",
				},
			},
			ticketsQueueSelect: {
				placeholder: "Queues",
				noQueue: "No queue",
			},
			tickets: {
				handledByAi: "Handled by AI: {{name}}",
				toasts: {
					deleted: "The service you were in has been deleted.",
					unauthorized: "Access not allowed",
				},
				filters: {
					user: "Filter by users",
					tags: "Filter by tags",
					connection: "Filter by connection",
					queues: "Filter by queues",
					emptyQueueWarning: "Select at least one queue (or 'No queue') to apply the filter.",
					tagsPlaceholder: "Filter by tags",
					tagsPopoverTitle: "Tags",
					tagsSearchPlaceholder: "Search",
					noTagsOption: "No tags",
					noTagsFound: "No tag found",
					singleSelected: "selected",
					multipleSelected: "selected",
					showingAll: "Showing all items",
					clear: "Clear",
					loadingTags: "Loading tags...",
				},
				notification: {
					message: "Message from",
				},
				tabs: {
					open: { title: "Open" },
					groups: { title: "Groups" },
					closed: { title: "Resolved" },
					search: { title: "Search" },
				},
				search: {
					placeholder: "Search tickets and messages",
				},
				buttons: {
					showAll: "All",
				},
			},
			transferTicketModal: {
				title: "Transfer Ticket",
				fieldLabel: "Type to search users",
				fieldUserLabel: "Agent",
				fieldQueueLabel: "Transfer to queue",
				fieldQueuePlaceholder: "Select a queue",
				userPlaceholder: "Select an agent",
				userPopoverTitle: "Agents",
				userSearchPlaceholder: "Search agents",
				clearUserOption: "Clear selection",
				noUsersFound: "No agent found",
				showingAllUsers: "Showing all agents",
				noOptions: "No user found with that name",
				noSelection: "Select an agent or a queue to transfer.",
				queuePopoverTitle: "Queues",
				queueSearchPlaceholder: "Search queues",
				noQueueOption: "No queue",
				noQueuesFound: "No queue found",
				singleSelected: "item selected",
				showingAll: "Showing all queues",
				buttons: {
					ok: "Transfer",
					cancel: "Cancel",
				},
			},
			ticketsList: {
				pendingHeader: "Waiting",
				pendingHeaderTab: "Pending",
				assignedHeader: "In Progress",
				noTicketsTitle: "Nothing here!",
				noTicketsMessage: "No service found with this status or search term",
				buttons: {
					accept: "Accept",
					closed: "Finish",
					reopen: "Reopen",
				},
			},
			ticketsListItem: {
				tooltip: {
					chatbot: "Chatbot",
					peek: "Peek Conversation",
					unreplied: "No reply for over 10 minutes",
				},
				noQueue: "NO QUEUE",
				viewLocation: "View location on map",
			},
			ticketAdvanced: {
				selectTicket: "Select Ticket",
				ticketNav: "Ticket",
				attendanceNav: "Services",
			},
			newTicketModal: {
				title: "Choose a contact",
				searchPlaceholder: "Search by name or phone",
				tagsPlaceholder: "Tags",
				tagsPopoverTitle: "Tags",
				tagsSearchPlaceholder: "Search",
				showingAll: "Showing all items",
				singleSelected: "item selected",
				multipleSelected: "items selected",
				clear: "Clear",
				noTagsOption: "No tags",
				noTagsFound: "No tags found",
				ticketTitle: "Choose a ticket",
				createTicket: "Create ticket",
				existingContactPlaceholder: "Search for an existing contact",
				contactSearchHint: "Type at least 3 characters to search for a contact",
				queuePlaceholder: "Select a queue",
				allQueuesOption: "All queues",
				queuePopoverTitle: "Queues",
				queueSearchPlaceholder: "Search queues",
				noQueueOption: "Create without queue",
				noQueuesFound: "No queue found",
				connectionPlaceholder: "Select a connection",
				allConnectionsOption: "All connections",
				connectionPopoverTitle: "Connections",
				loadingConnections: "Loading connections...",
				noConnectionsAvailable: "No connection available",
				newContact: "New contact",
				emptyState: "No contact found",
				emptyTicketsState: "No ticket found",
				loadingTags: "Loading tags...",
				buttons: {
					ok: "Save",
					cancel: "Cancel",
				},
			},
			locationPreview: {
				button: "Preview",
				openInMaps: "Open in Maps",
				showMap: "Show on map",
				hideMap: "Hide map",
			},
			mainDrawer: {
				listItems: {
					dashboard: "Dashboard",
					dashboardGeral: "Group Dashboard",
					connections: "Connections",
					tickets: "Services",
					quickMessages: "Quick Responses",
					tasks: "Tasks",
					contacts: "Contacts",
					queues: "Queues & Chatbot",
					tags: "Tags",
					academy: "Training",
					manageAcademy: "Manage Training",
					administration: "Administration",
					users: "Users",
					settings: "Settings",
					helps: "Help",
					messagesAPI: "API",
					schedules: "Schedules",
					campaigns: "Campaigns",
					flows: "Flows",
					annoucements: "Announcements",
					chats: "Internal Chat",
					financeiro: "Financial",
					files: "File List",
					prompts: "IA",
					queueIntegration: "Integrations",
					metaTemplates: "Meta Templates",
					integrations: "Integrations",
				},
				appBar: {
					refresh: "Reload page",
					notRegister: "No notifications",
					language: "Language",
					jobRole: "Job role",
					noJobRole: "No role",
					appearance: "Appearance",
					notificationVolume: "Notification Sound",
					greeting: {
						hello: "Hello",
						welcome: "Welcome to",
						active: "Active until",
					},
					user: {
						profile: "Profile",
						logout: "Logout",
					},
				},
			},
			queueIntegration: {
				title: "Integrations",
				table: {
					id: "ID",
					type: "Type",
					name: "Name",
					projectName: "Project Name",
					language: "Language",
					lastUpdate: "Last update",
					actions: "Actions",
				},
				buttons: {
					add: "Add Project",
				},
				searchPlaceholder: "Search...",
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "Are you sure? This action cannot be undone! It will be removed from linked queues and connections",
				},
			},
			files: {
				title: "File List",
				table: {
					name: "Name",
					contacts: "Contacts",
					actions: "Action",
				},
				toasts: {
					deleted: "List deleted successfully!",
					deletedAll: "All lists deleted successfully!",
				},
				buttons: {
					add: "Add",
					deleteAll: "Delete All",
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteAllTitle: "Delete All",
					deleteMessage: "Are you sure you want to delete this list?",
					deleteAllMessage: "Are you sure you want to delete all lists?",
				},
			},
			messagesAPI: {
				title: "API",
				labels: {
					doc: "Documentation for sending messages",
					method: "Sending Methods",
					textMessage: "Text Message",
					mediaMessage: "Media Message",
					instructions: "Instructions",
					observations: "Important notes",
					before1: "Before sending messages, you need to register the token linked to the connection that will send the messages.",
					before2: "To register, access the 'Connections' menu, click the edit button of the connection and enter the token in the appropriate field.",
					numberDescription: "The sending number should not have masks or special characters and must be composed of:",
					countryCode: "Country Code",
					number: "Number",
					textMessage2: "1. Text Messages",
					textMessageInstructions: "Below is the list of information needed to send text messages:",
					method2: "Method",
					e: "and",
					tests: "Send Test",
					mediaMessage2: "2. Media Messages",
				},
				textMessage: {
					number: "Number",
					body: "Message",
					token: "Registered Token",
				},
				mediaMessage: {
					number: "Number",
					body: "File name",
					media: "File",
					token: "Registered Token",
				},
				toasts: {
					unauthorized: "This company doesn't have permission to access this page! We are redirecting you.",
					success: "Message sent successfully!",
				},
				buttons: {
					send: "Send",
				},
			},
			notifications: {
				noTickets: "No notifications.",
			},
			quickMessages: {
				title: "Quick Responses",
				searchPlaceholder: "Search...",
				noAttachment: "No attachment",
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "This action is irreversible! Do you want to proceed?",
				},
				buttons: {
					add: "Add",
					attach: "Attach File",
					cancel: "Cancel",
					edit: "Edit",
				},
					toasts: {
						success: "Shortcut added successfully!",
						deleted: "Shortcut removed successfully!",
					},
					validation: {
						shortcodeRequired: "Please enter a shortcut.",
						messageRequired: "Please enter a quick response message.",
					},
					dialog: {
						title: "Quick Message",
						shortcode: "Shortcut",
					message: "Response",
					save: "Save",
					cancel: "Cancel",
					geral: "Allow edit",
					add: "Add",
					edit: "Edit",
					visao: "Allow view",
				},
				table: {
					shortcode: "Shortcut",
					message: "Message",
					actions: "Actions",
					mediaName: "File Name",
					status: "Status",
				},
			},
			leadSettings: {
				tabs: {
					apiTokens: "API Tokens",
					leadStatuses: "Lead Statuses",
				},
				tokens: {
					newToken: "New Token",
					webhookTitle: "Webhook URL for N8N",
					webhookHint: "Send the header:",
					copyUrl: "Copy URL",
					urlCopied: "Webhook URL copied!",
					emptyTitle: "No tokens created",
					emptyDescription: "Create an API token to integrate with N8N or other platforms",
					createdAt: "Created at:",
					deleteTitle: "Delete API Token",
					deleteMessage: "Are you sure you want to delete the token \"{name}\"? This action cannot be undone and all integrations using this token will stop working.",
					deleted: "Token deleted successfully",
				},
				statuses: {
					newStatus: "New Status",
					emptyTitle: "No statuses created",
					emptyDescription: "Create custom statuses to organize your leads",
					default: "Default",
					pending: "Pending",
					completed: "Completed",
					order: "Order",
					deleteTitle: "Delete Status",
					deleteMessage: "Are you sure you want to delete the status \"{name}\"? Leads with this status will be moved to the default status.",
					deleted: "Status deleted successfully",
				},
			},
			messageVariablesPicker: {
				label: "Available Variables",
				vars: {
					contactFirstName: "First Name",
					contactName: "Name",
					greeting: "Greeting",
					protocolNumber: "Protocol",
					date: "Date",
					hour: "Hour",
				},
			},
			contactLists: {
				title: "Contact Lists",
				table: {
					name: "Name",
					contacts: "Contacts",
					actions: "Actions",
				},
				buttons: {
					add: "New List",
				},
				dialog: {
					name: "Name",
					nameShort: "Short name",
					nameLong: "Long name",
					nameRequired: "Name is required",
					company: "Company",
					okEdit: "Edit",
					okAdd: "Add",
					add: "Add",
					edit: "Edit",
					cancel: "Cancel",
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "This action cannot be undone.",
				},
				toasts: {
					deleted: "Record deleted",
					success: "Operation completed successfully",
				},
			},
			contactListItems: {
				title: "Contacts",
				searchPlaceholder: "Search",
				buttons: {
					add: "New",
					lists: "Lists",
					import: "Import",
				},
				download: "Click here to download example spreadsheet.",
				dialog: {
					name: "Name",
					nameShort: "Short name",
					nameLong: "Long name",
					nameRequired: "Name is required",
					number: "Number",
					numberShort: "Short number",
					numberLong: "Long number",
					whatsapp: "WhatsApp",
					email: "Email",
					emailInvalid: "Invalid email",
					okEdit: "Edit",
					okAdd: "Add",
					add: "Add",
					edit: "Edit",
					cancel: "Cancel",
				},
				table: {
					name: "Name",
					number: "Number",
					whatsapp: "WhatsApp",
					email: "Email",
					actions: "Actions",
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "This action cannot be undone.",
					importMessage: "Do you want to import contacts from this spreadsheet? ",
					importTitle: "Import",
				},
				toasts: {
					deleted: "Record deleted",
				},
			},
			campaigns: {
				title: "Campaigns",
				searchPlaceholder: "Search",
				report: {
					title: "Report of",
					title2: "Campaign",
					of: "of",
					validContacts: "Valid contacts",
					delivered: "Delivered",
					connection: "Connection",
					contactList: "Contact List",
					schedule: "Schedule",
					conclusion: "Conclusion",
				},
				config: {
					interval: "Intervals",
					randomInterval: "Random Send Interval",
					biggerInterval: "Larger Interval After",
					greaterInterval: "Greater Send Interval",
					noInterval: "No Interval",
					second: "second",
					seconds: "seconds",
					notDefined: "Not defined",
					addVariable: "Add Variable",
					save: "Save Settings",
					shortcut: "Shortcut",
					content: "Content",
					close: "Close",
					add: "Add",
				},
				buttons: {
					add: "New Campaign",
					contactLists: "Contact Lists",
				},
				status: {
					inactive: "Inactive",
					programmed: "Scheduled",
					inProgress: "In progress",
					canceled: "Canceled",
					finished: "Finished",
				},
				table: {
					name: "Name",
					whatsapp: "Connection",
					contactList: "Contact List",
					status: "Status",
					scheduledAt: "Schedule",
					completedAt: "Completed",
					confirmation: "Confirmation",
					actions: "Actions",
					notDefined: "Not defined",
					notDefined2: "Not defined",
					notScheduled: "Not scheduled",
					notConcluded: "Not concluded",
					stopCampaign: "Stop Campaign",
				},
				dialog: {
					new: "New Campaign",
					update: "Edit Campaign",
					readonly: "View Only",
					form: {
						name: "Name",
						nameShort: "Short name",
						nameLong: "Long name",
						helper: "Use variables like {name}, {number}, {email} or define custom variables.",
						nameRequired: "Name is required",
						message1: "Message 1",
						message2: "Message 2",
						message3: "Message 3",
						message4: "Message 4",
						message5: "Message 5",
						messagePlaceholder: "Message content",
						whatsapp: "Connection",
						status: "Status",
						scheduledAt: "Schedule",
						confirmation: "Confirmation",
						contactList: "Contact List",
						tagList: "Tag List",
						fileList: "File List",
					},
					buttons: {
						add: "Add",
						edit: "Update",
						okadd: "Ok",
						cancel: "Cancel Sends",
						restart: "Restart Sends",
						close: "Close",
						attach: "Attach File",
					},
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "This action cannot be undone.",
				},
				toasts: {
					configSaved: "Settings saved",
					success: "Operation completed successfully",
					cancel: "Campaign canceled",
					restart: "Campaign restarted",
					deleted: "Record deleted",
				},
			},
			announcements: {
				active: "Active",
				inactive: "Inactive",
				title: "Announcements",
				searchPlaceholder: "Search",
				high: "High",
				medium: "Medium",
				low: "Low",
				buttons: {
					add: "New Announcement",
					contactLists: "Announcement Lists",
				},
				table: {
					priority: "Priority",
					title: "Title",
					text: "Text",
					mediaName: "File",
					status: "Status",
					actions: "Actions",
				},
				dialog: {
					edit: "Edit Announcement",
					add: "New Announcement",
					update: "Edit Announcement",
					readonly: "View Only",
					form: {
						priority: "Priority",
						required: "Required field",
						title: "Title",
						text: "Text",
						mediaPath: "File",
						status: "Status",
					},
					buttons: {
						add: "Add",
						edit: "Update",
						okadd: "Ok",
						cancel: "Cancel",
						close: "Close",
						attach: "Attach File",
					},
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "This action cannot be undone.",
				},
				toasts: {
					success: "Operation completed successfully",
					deleted: "Record deleted",
					info: "This company doesn't have permission to access this page! We are redirecting you.",
				},
			},
			campaignsConfig: {
				title: "Campaign Settings",
			},
			queues: {
				title: "Queues & Chatbot",
				table: {
					id: "ID",
					name: "Name",
					color: "Color",
					greeting: "Greeting message",
					actions: "Actions",
					orderQueue: "Queue order (bot)",
				},
				buttons: {
					add: "Add queue",
				},
				emptyState: "No queues found",
				toasts: {
					success: "Queue deleted successfully.",
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "Are you sure? This action cannot be undone! Services in this queue will continue to exist but won't have any queue assigned.",
				},
			},
			queueSelect: {
				inputLabel: "Queues",
			},
			users: {
				title: "Users",
				table: {
					id: "ID",
					name: "Name",
					email: "Email",
					profile: "Profile",
					actions: "Actions",
					admin: "Admin",
					user: "User",
				},
				buttons: {
					add: "Add user",
				},
				seatLimitReached: "You've reached your plan's user limit. Upgrade in Settings → Billing to add more.",
				emptyState: "No users found",
				toasts: {
					created: "User created successfully.",
					deleted: "User deleted successfully.",
					updated: "User updated successfully.",
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "All user data will be lost. Open services for this user will be moved to the queue.",
				},
			},
			todolist: {
				input: "New task",
				buttons: {
					add: "Add",
					save: "Save",
				},
			},
			helps: {
				title: "Help Center",
			},
			schedules: {
				title: "Schedules",
				confirmationModal: {
					deleteTitle: "Are you sure you want to delete this Schedule?",
					deleteMessage: "This action cannot be undone.",
				},
				table: {
					contact: "Contact",
					body: "Message",
					sendAt: "Schedule Date",
					sentAt: "Send Date",
					status: "Status",
					actions: "Actions",
				},
				messages: {
					date: "Date",
					time: "Time",
					event: "Event",
					allDay: "All Day",
					week: "Week",
					work_week: "Schedules",
					day: "Day",
					month: "Month",
					previous: "Previous",
					next: "Next",
					yesterday: "Yesterday",
					tomorrow: "Tomorrow",
					today: "Today",
					agenda: "Agenda",
					noEventsInRange: "No schedules in this period.",
					showMore: "more",
				},
				buttons: {
					add: "New Schedule",
				},
				toasts: {
					deleted: "Schedule deleted successfully.",
				},
			},
			tags: {
				title: "Tags",
				confirmationModal: {
					deleteTitle: "Are you sure you want to delete this Tag?",
					deleteMessage: "This action cannot be undone.",
					deleteAllMessage: "Are you sure you want to delete all Tags?",
					deleteAllTitle: "Delete All",
				},
				table: {
					name: "Name",
					color: "Color",
					tickets: "Tagged Records",
					actions: "Actions",
				},
				buttons: {
					add: "New Tag",
					deleteAll: "Delete All",
				},
				toasts: {
					deletedAll: "All Tags deleted successfully!",
					deleted: "Tag deleted successfully.",
				},
			},
			settings: {
				schedulesUpdated: "Schedules updated successfully.",
				success: "Settings saved successfully.",
				title: "Settings",
				tabs: {
					options: "Options",
					jobRoles: "Job Roles",
					security: "Security",
					schedules: "Schedules",
					companyData: "Company Data",
					billing: "Billing",
					companies: "Companies",
					plans: "Plans",
					helps: "Help",
				},
				companyData: {
					title: "Company Data",
					subtitle: "Tax/legal details used for billing and documents.",
					accountLabel: "Account",
					razaoSocial: "Legal Name",
					nomeFantasia: "Trade Name",
					cnpj: "Tax ID (CNPJ)",
					phone: "Phone",
					email: "Email",
					phRazaoSocial: "e.g. Acme Trading LLC",
					phNomeFantasia: "e.g. Acme Store",
					phEmail: "billing@company.com",
					save: "Save",
					saved: "Data updated successfully.",
					primaryBadge: "Primary billing account",
					primaryHint: "This account's Tax ID and email feed billing (Pagar.me) and the panel order.",
					invalidCnpj: "Invalid CNPJ. Check the digits.",
				},
				jobRoles: {
					title: "Job Roles",
					emptyState: "No job roles created for this company yet.",
					modal: {
						add: "New job role",
						edit: "Edit job role",
					},
					form: {
						name: "Job role name",
					},
					buttons: {
						add: "Add job role",
						cancel: "Cancel",
						save: "Save",
					},
					toasts: {
						created: "Job role created successfully.",
						updated: "Job role updated successfully.",
						deleted: "Job role deleted successfully.",
					},
					confirmationModal: {
						deleteTitle: "Delete job role",
						deleteMessage: "Users assigned to this role will automatically move to No role.",
					},
				},
				options: {
					toasts: {
						success: "Operation updated successfully.",
						webNotificationsUnsupported: "This browser does not support web notifications.",
						webNotificationsPermissionDenied: "Notification permission was not granted in the browser.",
					},
					fields: {
						ratings: {
							title: "Ratings",
							disabled: "Disabled",
							enabled: "Enabled",
						},
						expedientManager: {
							title: "Business Hours Management",
							queue: "Queue",
							company: "Company",
						},
						ignoreMessages: {
							title: "Ignore Group Messages",
						},
						acceptCall: {
							title: "Accept Call",
							disabled: "Don't accept",
							enabled: "Accept",
						},
						chatbotType: {
							title: "Chatbot Type",
							text: "Text",
						},
						sendGreetingMessageOneQueues: {
							title: "Send greeting when there's only 1 queue",
						},
						quickMessagesScope: {
							title: "Quick Responses",
							description: "Define if quick responses are shared by user or company",
							user: "By User",
							company: "By Company",
						},
						noResponseBusinessHours: {
							title: "Business Hours Chart Filter",
							description: "Filter the no-response history chart to show only configured business hours",
						},
						webNotifications: {
							title: "Web Notifications",
							description: "Show browser notifications for new ticket messages",
						},
						internalChatNotifications: {
							title: "Internal Chat Notifications",
							description: "Show browser notifications for DMs and mentions in Internal Chat",
						},
						enableAcademy: {
							title: "Training Area",
							description: "Show the Training (Academy) page for this company",
						},
						disabled: "Disabled",
						active: "Active",
						enabled: "Enabled",
						artThresholds: {
							title: "ART — Response Time",
							description: "Set minute thresholds to classify average response time (green, yellow, red).",
							yellowLabel: "Yellow limit (min)",
							redLabel: "Red limit (min)",
							invalidRange: "Red limit must be greater than yellow (1–1440).",
						},
					},
					updating: "Updating...",
					tabs: {
						integrations: "INTEGRATIONS",
					},
				},
				helps: {
					toasts: {
						errorList: "Could not load records list",
						errorOperation: "Could not complete operation",
						error: "Could not complete operation. Check if help with the same name exists or if fields were filled correctly",
						success: "Operation completed successfully!",
					},
					buttons: {
						clean: "Clear",
						delete: "Delete",
						save: "Save",
					},
					grid: {
						title: "Title",
						description: "Description",
						video: "Video",
					},
					confirmModal: {
						title: "Delete Record",
						confirm: "Do you really want to delete this record?",
					},
				},
				company: {
					toasts: {
						errorList: "Could not load records list",
						errorOperation: "Could not complete operation",
						error: "Could not complete operation. Check if company with same name exists or if fields were filled correctly",
						success: "Operation completed successfully!",
					},
					confirmModal: {
						title: "Delete Record",
						confirm: "Do you really want to delete this record?",
					},
					form: {
						name: "Name",
						email: "Email",
						phone: "Phone",
						plan: "Plan",
						status: "Status",
						yes: "Yes",
						no: "No",
						campanhas: "Campaigns",
						enabled: "Enabled",
						disabled: "Disabled",
						createdAt: "Created On",
					},
					buttons: {
						clear: "Clear",
						delete: "Delete",
						user: "User",
						save: "Save",
					},
				},
				schedules: {
					form: {
						weekday: "Weekday",
						initialHour: "Start Time",
						finalHour: "End Time",
						save: "Save",
					},
				},
				settings: {
					userCreation: {
						name: "User creation",
						options: {
							enabled: "Enabled",
							disabled: "Disabled",
						},
					},
				},
			},
			messagesList: {
				header: {
					assignedTo: "Assigned to:",
					buttons: {
						return: "Return",
						resolve: "Resolve",
						reopen: "Reopen",
						accept: "Accept",
						download: "Download",
					},
				},
				lostCall: "Missed voice/video call at",
				deletedMessage: "This message was deleted by the contact",
				edited: "Edited",
				audio: "Audio",
				location: "Location",
				saudation: "Say hello to your new contact!",
			},
			messagesInput: {
				placeholderOpen: "Type a message",
				placeholderClosed: "Reopen or accept this ticket to send a message.",
				signMessage: "Sign",
			},
			contactDrawer: {
				header: "Contact Information",
				buttons: {
					edit: "Edit contact",
				},
				extraInfo: "Other information",
			},
			fileModal: {
				title: {
					add: "Add file list",
					edit: "Edit file list",
				},
				buttons: {
					okAdd: "Save",
					okEdit: "Edit",
					cancel: "Cancel",
					fileOptions: "Add file",
				},
				form: {
					name: "File list name",
					message: "List details",
					fileOptions: "File list",
					extraName: "Message to send with file",
					extraValue: "Option value",
				},
				formErrors: {
					name: {
						required: "Name is required",
						short: "Name is too short",
					},
					message: {
						required: "Message is required",
					},
				},
				success: "File list saved successfully!",
			},
			ticketOptionsMenu: {
				schedule: "Schedule",
				delete: "Delete",
				transfer: "Transfer",
				registerAppointment: "Contact Notes",
				appointmentsModal: {
					title: "Contact Notes",
					textarea: "Note",
					placeholder: "Enter the information you want to record here",
				},
				confirmationModal: {
					title: "Delete ticket",
					titleFrom: "from contact ",
					message: "Warning! All messages related to this ticket will be lost.",
				},
				buttons: {
					delete: "Delete",
					cancel: "Cancel",
				},
			},
			confirmationModal: {
				buttons: {
					confirm: "Ok",
					cancel: "Cancel",
				},
			},
			messageOptionsMenu: {
				delete: "Delete",
				reply: "Reply",
				forward: "Forward",
				edit: "Edit",
				retry: "Retry",
				retrying: "Retrying...",
				confirmationModal: {
					title: "Delete message?",
					message: "This action cannot be undone.",
				},
			},
			editMessageModal: {
				title: "Edit message",
				originalMessage: "Original message:",
				newMessage: "New message",
				characters: "characters",
				timeRemaining: "Time remaining",
				timeExpired: "Edit time expired",
				success: "Message edited successfully!",
				errors: {
					emptyMessage: "Message cannot be empty",
					noChanges: "No changes detected",
					timeExpired: "Edit time expired (10 minute limit)",
				},
				buttons: {
					cancel: "Cancel",
					save: "Save",
				},
			},
			templateModal: {
				title: "Select Template",
				searchPlaceholder: "Search by name or content...",
				loading: "Loading templates...",
				recents: "Recent",
				favorites: "Favorites",
				favorite: "Favorite template",
				unfavorite: "Remove from favorites",
				all: "All",
				previewEmpty: "Select a template to see the preview",
				sentToast: "Template \"{name}\" sent successfully!",
				categories: {
					all: "All",
					marketing: "Marketing",
					utility: "Utility",
					auth: "Authentication",
					other: "Other",
				},
				empty: {
					title: "No templates found",
					description: "Configure approved templates in your Meta for Developers account to use this feature.",
				},
				noResults: {
					title: "No results",
					description: "Try another search or remove the category filter.",
				},
				hints: {
					navigate: "navigate",
					send: "send",
					close: "close",
				},
				buttons: {
					cancel: "Cancel",
					send: "Send template",
					sending: "Sending...",
				},
			},
			academy: {
				title: "Training",
				empty: "No courses available yet.",
				lessons: "lessons",
				progress: "complete",
				courseContent: "Course content",
				ofLessonsCompleted: "of {{total}} completed",
				lessonTag: "LESSON",
				back: "Back",
				videoUnavailable: "Video unavailable",
				continueWatching: "Continue watching",
				courseCompleted: "Course completed",
				markComplete: "Mark as complete",
				unmarkComplete: "Unmark",
				toasts: {
					disabled: "The Training area is disabled.",
					noPermission: "You don't have permission to access this area.",
				},
				manage: {
					title: "Manage Training",
					draft: "Draft",
					buttons: {
						cancel: "Cancel",
						save: "Save",
						addCourse: "New course",
						addModule: "New module",
					},
					validation: {
						titleRequired: "Enter a title (min. 2 characters).",
						videoUrlRequired: "Enter the video URL.",
					},
					toasts: {
						courseSaved: "Course saved.",
						moduleSaved: "Module saved.",
						lessonSaved: "Lesson saved.",
						thumbnailRemoved: "Thumbnail removed.",
					},
					confirm: {
						deleteCourse: "Delete course",
						deleteModule: "Delete module",
						deleteLesson: "Delete lesson",
						message: "This action cannot be undone.",
					},
					courseModal: {
						addTitle: "New course",
						editTitle: "Edit course",
						title: "Title",
						description: "Description",
						published: "Published",
						thumbnail: "Thumbnail (image)",
						removeThumbnail: "Remove",
					},
					moduleModal: {
						addTitle: "New module",
						editTitle: "Edit module",
						title: "Title",
					},
					lessonModal: {
						addTitle: "New lesson",
						editTitle: "Edit lesson",
						title: "Title",
						description: "Description",
						videoUrl: "Video URL (YouTube or Vimeo)",
						currentVideo: "Current video",
						keepHint: "leave the URL blank to keep it",
					},
				},
			},
			backendErrors: {
				ERR_ACADEMY_DISABLED: "The Training area is disabled for this company.",
				ERR_ACADEMY_INVALID_VIDEO_URL: "Invalid video URL. Use a YouTube or Vimeo link.",
				ERR_ACADEMY_COURSE_NOT_FOUND: "Course not found.",
				ERR_ACADEMY_MODULE_NOT_FOUND: "Module not found.",
				ERR_ACADEMY_LESSON_NOT_FOUND: "Lesson not found.",
				ERR_INTERNAL_SERVER_ERROR: "An unexpected error occurred. Please try again later",
				ERR_NO_OTHER_WHATSAPP: "There must be at least one default WhatsApp.",
				ERR_NO_DEF_WAPP_FOUND: "No default WhatsApp found. Check the connections page.",
				ERR_WAPP_NOT_INITIALIZED: "This WhatsApp session hasn't been initialized. Check the connections page.",
				ERR_WAPP_CHECK_CONTACT: "Couldn't verify WhatsApp contact. Check the connections page",
				ERR_WAPP_INVALID_CONTACT: "This is not a valid WhatsApp number.",
				ERR_WAPP_DOWNLOAD_MEDIA: "Couldn't download WhatsApp media. Check the connections page.",
				ERR_INVALID_CREDENTIALS: "Invalid username or password",
				ERR_USER_DONT_EXISTS: "User not found. Check the provided email.",
				ERR_SENDING_WAPP_MSG: "Error sending WhatsApp message. Check the connections page.",
				ERR_DELETE_WAPP_MSG: "Couldn't delete WhatsApp message.",
				ERR_EDIT_WAPP_MSG: "Couldn't edit WhatsApp message.",
				ERR_EDIT_ONLY_OWN_MESSAGES: "You can only edit your own messages.",
				ERR_EDIT_ONLY_WAHA_PROVIDER: "Message editing is only available for WAHA connections.",
				ERR_EDIT_TIME_LIMIT_EXCEEDED: "Edit time expired (10 minute limit).",
				ERR_CANNOT_EDIT_DELETED_MESSAGE: "Cannot edit a deleted message.",
				ERR_CANNOT_EDIT_MEDIA_MESSAGE: "Cannot edit messages with media.",
				ERR_EMPTY_MESSAGE_BODY: "Message cannot be empty.",
				ERR_OTHER_OPEN_TICKET: "There's already an open ticket for this contact.",
				ERR_SESSION_EXPIRED: "Session expired. Please log in.",
				ERR_USER_CREATION_DISABLED: "User creation has been disabled by the administrator.",
				ERR_NO_PERMISSION: "You don't have permission to access this resource.",
				ERR_CANNOT_REMOVE_ACTIVE_COMPANY: "Cannot remove the user's active Account. Set another Account as primary first.",
				ERR_COMPANY_NOT_ASSIGNED: "This Account is not linked to the user.",
				ERR_COMPANY_ALREADY_ASSIGNED: "This Account is already linked to the user.",
				ERR_COMPANY_NOT_IN_GROUP: "The Account must belong to the same Company as the user.",
				ERR_DUPLICATED_CONTACT: "A contact with this number already exists.",
				ERR_NO_SETTING_FOUND: "No settings found with this ID.",
				ERR_NO_CONTACT_FOUND: "No contact found with this ID.",
				ERR_NO_TICKET_FOUND: "No ticket found with this ID.",
				ERR_MAX_PINNED_TICKETS: "Maximum of 3 pinned conversations reached.",
				ERR_NO_USER_FOUND: "No user found with this ID.",
				ERR_NO_WAPP_FOUND: "No WhatsApp found with this ID.",
				ERR_TICKET_NO_CONNECTION: "This ticket has no WhatsApp connection defined. Select a connection before resolving.",
				ERR_CREATING_MESSAGE: "Error creating message in database.",
				ERR_CREATING_TICKET: "Error creating ticket in database.",
				ERR_FETCH_WAPP_MSG: "Error fetching WhatsApp message, it might be too old.",
				ERR_QUEUE_COLOR_ALREADY_EXISTS: "This color is already in use, choose another.",
				ERR_WAPP_GREETING_REQUIRED: "Greeting message is required when there is more than one queue.",
			},
			internalChat: {
				jobRoleRequired: {
					title: "Select your role",
					intro: "To use Internal Chat, choose the role that best represents your function in the company. It will appear next to your name in conversations.",
					empty: "No roles registered. Ask the administrator to register roles in Settings → Roles.",
					confirm: "Confirm",
					saving: "Saving...",
					toastSaved: "Role defined",
				},
			},
		}
	},
};

export { messages };
